import { extintorService } from './extintorService';
import { puestoService } from './puestoService';
import { elementoService } from './elementoService';
import { getFormattedRemitoNumber } from './remitoService';
import { getFormattedOrdenNumber } from './ordenTrabajoService';
import { apiRequest, createUrlWithCenterId } from '../utils/apiHelpers';
import { isAbortError } from '../utils/listSearch';

export type GlobalSearchEntityType =
  'cliente' | 'extintor' | 'puesto' | 'elemento' | 'remito' | 'ordenTrabajo';

export type GlobalSearchItem = {
  type: GlobalSearchEntityType;
  id: number;
  title: string;
  subtitle?: string;
  path: string;
};

export type GlobalSearchGroup = {
  type: GlobalSearchEntityType;
  label: string;
  items: GlobalSearchItem[];
};

const TAKE_PER_GROUP = 5;

const GROUP_LABELS: Record<GlobalSearchEntityType, string> = {
  cliente: 'Clientes',
  extintor: 'Extintores',
  puesto: 'Puestos',
  elemento: 'Elementos',
  remito: 'Remitos',
  ordenTrabajo: 'Órdenes de trabajo',
};

const GROUP_ORDER: GlobalSearchEntityType[] = [
  'cliente',
  'extintor',
  'puesto',
  'elemento',
  'remito',
  'ordenTrabajo',
];

function take<T>(rows: T[] | null | undefined, n = TAKE_PER_GROUP): T[] {
  return (rows || []).slice(0, n);
}

async function searchClientes(q: string, signal?: AbortSignal): Promise<GlobalSearchItem[]> {
  const endpoint = createUrlWithCenterId('/api/clientes', { q });
  const rows = await apiRequest(endpoint, { signal });
  return take(rows).map((c: any) => {
    const id = c.id ?? c.Id;
    const nombre = c.nombre ?? c.Nombre ?? `Cliente #${id}`;
    const cuit = c.cuit ?? c.CUIT;
    return {
      type: 'cliente' as const,
      id,
      title: nombre,
      subtitle: cuit ? `CUIT ${cuit}` : undefined,
      path: `/clientes/${id}`,
    };
  });
}

async function searchExtintores(q: string, signal?: AbortSignal): Promise<GlobalSearchItem[]> {
  const result = await extintorService.list({ q, page: 1, pageSize: TAKE_PER_GROUP }, signal);
  return take(result?.data).map((e) => ({
    type: 'extintor' as const,
    id: e.id,
    title:
      e.codigo || e.nroSerie || (e.interno != null ? `Interno ${e.interno}` : `Extintor #${e.id}`),
    subtitle: e.sucursal?.nombre || undefined,
    path: `/extintores/${e.id}`,
  }));
}

async function searchPuestos(q: string, signal?: AbortSignal): Promise<GlobalSearchItem[]> {
  const result = await puestoService.list({ q, page: 1, pageSize: TAKE_PER_GROUP }, signal);
  return take(result?.data).map((p) => ({
    type: 'puesto' as const,
    id: p.id,
    title: p.nombre || p.codigo || `Puesto #${p.id}`,
    subtitle: p.sucursal?.nombre || p.ubicacion || undefined,
    path: `/puestos/${p.id}`,
  }));
}

async function searchElementos(q: string, signal?: AbortSignal): Promise<GlobalSearchItem[]> {
  const result = await elementoService.list({ q, page: 1, pageSize: TAKE_PER_GROUP }, signal);
  return take(result?.data).map((e) => ({
    type: 'elemento' as const,
    id: e.id,
    title: e.codigo || (e.interno != null ? `Interno ${e.interno}` : `Elemento #${e.id}`),
    subtitle:
      [e.tipoDeElemento?.nombre, e.sucursal?.nombre].filter(Boolean).join(' · ') || undefined,
    path: `/elementos/${e.id}`,
  }));
}

async function searchRemitos(q: string, signal?: AbortSignal): Promise<GlobalSearchItem[]> {
  const endpoint = createUrlWithCenterId('/api/Remitos', { q });
  const rows = await apiRequest(endpoint, { signal });
  return take(rows).map((r: any) => {
    const remito = {
      id: r.id ?? r.Id,
      letra: r.letra ?? r.Letra,
      secuencia: r.secuencia ?? r.Secuencia,
      numero: r.numero ?? r.Numero ?? 0,
      sucursal: r.sucursal ?? r.Sucursal,
    };
    return {
      type: 'remito' as const,
      id: remito.id,
      title: getFormattedRemitoNumber(remito as any),
      subtitle: remito.sucursal?.nombre || remito.sucursal?.cliente?.nombre || undefined,
      path: `/remitos/${remito.id}`,
    };
  });
}

async function searchOrdenes(q: string, signal?: AbortSignal): Promise<GlobalSearchItem[]> {
  const endpoint = createUrlWithCenterId('/api/OrdenesTrabajo', { q });
  const rows = await apiRequest(endpoint, { signal });
  return take(rows).map((o: any) => {
    const orden = {
      id: o.id ?? o.Id,
      numero: o.numero ?? o.Numero ?? 0,
      sucursal: o.sucursal ?? o.Sucursal,
      observaciones: o.observaciones ?? o.Observaciones,
    };
    return {
      type: 'ordenTrabajo' as const,
      id: orden.id,
      title: getFormattedOrdenNumber(orden as any),
      subtitle:
        orden.sucursal?.nombre ||
        (typeof orden.observaciones === 'string' ? orden.observaciones.slice(0, 60) : undefined),
      path: `/ordenes-trabajo/${orden.id}`,
    };
  });
}

type SearchFn = (q: string, signal?: AbortSignal) => Promise<GlobalSearchItem[]>;

const SEARCHERS: { type: GlobalSearchEntityType; run: SearchFn }[] = [
  { type: 'cliente', run: searchClientes },
  { type: 'extintor', run: searchExtintores },
  { type: 'puesto', run: searchPuestos },
  { type: 'elemento', run: searchElementos },
  { type: 'remito', run: searchRemitos },
  { type: 'ordenTrabajo', run: searchOrdenes },
];

export async function searchGlobal(q: string, signal?: AbortSignal): Promise<GlobalSearchGroup[]> {
  const term = q.trim();
  if (term.length < 3) {
    return [];
  }

  const results = await Promise.allSettled(SEARCHERS.map(({ run }) => run(term, signal)));

  if (signal?.aborted) {
    const abortError = new DOMException('Aborted', 'AbortError');
    throw abortError;
  }

  const groups: GlobalSearchGroup[] = [];
  results.forEach((result, index) => {
    const type = SEARCHERS[index].type;
    if (result.status === 'rejected') {
      if (isAbortError(result.reason)) throw result.reason;
      return;
    }
    if (result.value.length === 0) return;
    groups.push({
      type,
      label: GROUP_LABELS[type],
      items: result.value,
    });
  });

  return GROUP_ORDER.map((type) => groups.find((g) => g.type === type)).filter(
    (g): g is GlobalSearchGroup => Boolean(g),
  );
}
